import { registerExactEvmScheme } from "@x402/evm/exact/client";
import { wrapFetchWithPayment, x402Client } from "@x402/fetch";

/**
 * Build a payment-wrapped fetch bound to the connected browser wallet so the
 * app can pay an x402 endpoint inline — no copy-paste, no agentcash CLI. The
 * wrapper POSTs the request, and on a 402 it has the wallet sign an EIP-3009
 * `TransferWithAuthorization` (a gasless USDC authorization) and retries with
 * the payment header. Uses the same @x402 v2 packages the worker runs.
 *
 * The x402 `ClientEvmSigner` shape is `{ address, signTypedData, readContract }`,
 * assembled here from a wagmi wallet client (signing) plus a public client
 * (USDC allowance/nonce reads). Both are typed loosely on purpose: this is a
 * signer bridge across viem's cross-version generics, and the runtime contract
 * is the three methods above, not viem's client type identity.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function walletPaidFetch(walletClient: any, publicClient: any) {
  const account = walletClient?.account;
  if (!account) throw new Error("The wallet has no active account.");
  const signer = {
    address: account.address as `0x${string}`,
    signTypedData: (msg: {
      domain: Record<string, unknown>;
      types: Record<string, unknown>;
      primaryType: string;
      message: Record<string, unknown>;
    }): Promise<`0x${string}`> =>
      walletClient.signTypedData({
        account,
        domain: msg.domain,
        types: msg.types,
        primaryType: msg.primaryType,
        message: msg.message,
      }),
    readContract: (args: {
      address: `0x${string}`;
      abi: readonly unknown[];
      functionName: string;
      args?: readonly unknown[];
    }): Promise<unknown> => publicClient.readContract(args),
  };
  const client = new x402Client();
  registerExactEvmScheme(client, { signer });
  return wrapFetchWithPayment(globalThis.fetch.bind(globalThis), client);
}
